/**
 * Central Calculation Helpers - Phase 14 Production QA Audit
 * All calculations verified for accuracy, rounding, and consistency
 */
import { demoRepository } from '../repository/demoRepository';
import { getNextContractedChangeDate } from './leasePosition';

/**
 * Round money values to 2 decimal places (never truncate)
 */
export const roundMoney = (value) => {
  if (value === null || value === undefined || isNaN(value)) return 0.00;
  return Math.round(value * 100) / 100;
};

/**
 * Round percentages to 2 decimal places
 */
export const roundPercent = (value) => {
  if (value === null || value === undefined || isNaN(value)) return 0.00;
  return Math.round(value * 10000) / 10000;
};

/**
 * Format currency as AUD with 2 decimal places
 */
export const formatCurrencyAUD = (value) => {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(roundMoney(value));
};

/**
 * Format number with 2 decimal places (no currency symbol)
 */
export const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-AU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(roundMoney(value));
};

/**
 * Format percentage with 2 decimal places
 */
export const formatPercentage = (value, showPercentSign = true) => {
  const rounded = roundPercent(value);
  const formatted = new Intl.NumberFormat('en-AU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(rounded * 100);
  return showPercentSign ? `${formatted}%` : formatted;
};

/**
 * Format date as DD-MM-YYYY (Australian format with dashes)
 * Example: 01-07-2026
 */
export const formatDateAU = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

/**
 * Format date with day name as Day, DD-MM-YYYY
 * Example: Wednesday, 01-07-2026
 */
export const formatDateWithDayAU = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return `${days[d.getDay()]},${formatDateAU(d)}`;
};

/**
 * Get Australian Financial Year (July 1 - June 30)
 * Format: YYYY-YY (e.g., 2024-25)
 */
export const getAustralianFinancialYear = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  const year = d.getFullYear();
  const month = d.getMonth() + 1;
  // Australian FY starts July 1
  if (month >= 7) {
    return `${year}-${String(year + 1).slice(-2)}`;
  } else {
    return `${year - 1}-${String(year).slice(-2)}`;
  }
};

/**
 * Calculate tenure from start date to now
 */
export const calculateTenure = (startDate) => {
  if (!startDate) return '-';
  const start = new Date(startDate);
  const now = new Date();
  const years = now.getFullYear() - start.getFullYear();
  const months = now.getMonth() - start.getMonth();
  const totalMonths = (years * 12) + months;
  if (totalMonths < 0) return 'Less than a month';
  if (totalMonths < 12) return `${totalMonths} month${totalMonths !== 1 ? 's' : ''}`;
  const y = Math.floor(totalMonths / 12);
  const m = totalMonths % 12;
  return `${y} year${y !== 1 ? 's' : ''}${m > 0 ? ` ${m} month${m !== 1 ? 's' : ''}` : ''}`;
};

/**
 * Calculate days until expiry date
 */
export const calculateDaysToExpiry = (endDate) => {
  if (!endDate) return 0;
  const end = new Date(endDate);
  const now = new Date();
  const diffMs = end.getTime() - now.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
};

/**
 * Canonical rent-basis conversion factors.
 * PocketBase currently supports weekly, fortnightly, monthly and custom rent bases.
 * Quarterly/annual are also understood here for compatibility with imported data.
 */
export const RENT_BASIS_PERIODS_PER_YEAR = Object.freeze({
  weekly: 52,
  fortnightly: 26,
  monthly: 12,
  quarterly: 4,
  annual: 1
});

export const getRentBasisPeriodsPerYear = (rentBasis = 'weekly') => {
  const basis = String(rentBasis || 'weekly').trim().toLowerCase();
  return RENT_BASIS_PERIODS_PER_YEAR[basis] || 0;
};

/**
 * Return an auditable rent annualisation breakdown.
 * The stored rent amount is interpreted according to rentBasis; it is not
 * assumed to be weekly merely because the legacy frontend field is named weeklyRent.
 */
export const getRentAnnualisation = (rentAmount, rentBasis = 'weekly') => {
  const amount = Number(rentAmount);
  const basis = String(rentBasis || 'weekly').trim().toLowerCase();
  const periodsPerYear = getRentBasisPeriodsPerYear(basis);
  const validAmount = Number.isFinite(amount) && amount >= 0;

  if (!validAmount || periodsPerYear === 0) {
    return {
      supported: false,
      rentAmount: validAmount ? roundMoney(amount) : 0,
      rentBasis: basis,
      periodsPerYear,
      annualRent: 0,
      weeklyEquivalent: 0,
      monthlyEquivalent: 0
    };
  }

  const annualRent = roundMoney(amount * periodsPerYear);
  return {
    supported: true,
    rentAmount: roundMoney(amount),
    rentBasis: basis,
    periodsPerYear,
    annualRent,
    weeklyEquivalent: roundMoney(annualRent / 52),
    monthlyEquivalent: roundMoney(annualRent / 12)
  };
};

/**
 * Calculate annualised rent from an amount and its recorded rent basis.
 */
export const calculateAnnualizedRent = (rentAmount, rentBasis = 'weekly') => {
  return getRentAnnualisation(rentAmount, rentBasis).annualRent;
};

/**
 * Calculate total property acquisition cost
 */
export const calculatePropertyAcquisitionCost = (prop) => {
  if (!prop) return 0;
  const legacyCosts = prop.acquisitionCosts || 0;
  const advancedCosts = (prop.stampDuty || 0) + (prop.legalFees || 0) + (prop.buyerAgentFees || 0) + (prop.bankSetupCosts || 0) + (prop.buildingPestCosts || 0) + (prop.initialRepairs || 0) + (prop.otherAcquisitionCosts || 0);
  return roundMoney((prop.acquisitionPrice || 0) + Math.max(legacyCosts, advancedCosts));
};

/**
 * Calculate portfolio current market value
 */
export const calculatePortfolioCurrentMarketValue = (properties) => {
  return roundMoney(properties.reduce((sum, p) => sum + (p.currentMarketValue || 0), 0));
};

/**
 * Calculate portfolio acquisition value
 */
export const calculatePortfolioAcquisitionValue = (properties) => {
  return roundMoney(properties.reduce((sum, p) => sum + calculatePropertyAcquisitionCost(p), 0));
};

/**
 * Canonical transaction categories used by PocketBase and by the calculation engine.
 * Legacy display labels are normalised so older/demo records continue to classify safely.
 */
export const TRANSACTION_CATEGORY_ALIASES = Object.freeze({
  'bond claim': 'bond_claim',
  'insurance payout': 'insurance_payout',
  'other income': 'other_income',
  'management fee': 'agent_management_fee',
  'agent management fee': 'agent_management_fee',
  'letting fee': 'letting_fee',
  'renewal fee': 'renewal_fee',
  'admin fee': 'admin_fee',
  'inspection fee': 'inspection_fee',
  'marketing': 'marketing_fee',
  'marketing fee': 'marketing_fee',
  'council rates': 'council',
  'water rates': 'water',
  'strata fees': 'strata',
  'insurance premium': 'insurance',
  'insurance excess': 'insurance',
  'loan interest': 'loan_interest',
  'loan fee': 'loan_fees',
  'loan fees': 'loan_fees',
  'bank fees': 'bank_fees',
  'borrowing cost': 'borrowing_cost',
  'capital improvement': 'capital_improvement',
  'depreciation/capital works': 'capital_works',
  'capital works': 'capital_works',
  'stamp duty': 'stamp_duty',
  'legal fees': 'legal_conveyancing',
  'legal conveyancing': 'legal_conveyancing',
  'buyer agent fees': 'buyer_agent',
  'buyer agent': 'buyer_agent',
  'building pest': 'building_pest',
  'purchase price': 'acquisition_cost',
  'acquisition cost': 'acquisition_cost',
  'private/non-deductible': 'private_non_deductible',
  'private non deductible': 'private_non_deductible',
  'accountant review': 'accountant_review',
  'other expense': 'other_expense'
});

export const normaliseTransactionCategory = (category) => {
  const raw = String(category || '').trim().toLowerCase();
  if (!raw) return '';
  return TRANSACTION_CATEGORY_ALIASES[raw] || raw.replace(/\s+/g, '_');
};

export const OPERATING_INCOME_CATEGORIES = Object.freeze(new Set([
  'rent', 'reimbursement'
]));

export const OPERATING_EXPENSE_CATEGORIES = Object.freeze(new Set([
  'agent_management_fee', 'letting_fee', 'renewal_fee', 'admin_fee', 'inspection_fee', 'marketing_fee',
  'council', 'water', 'strata', 'repairs', 'maintenance', 'insurance', 'utilities', 'cleaning', 'gardening'
]));

export const NON_OPERATING_INCOME_CATEGORIES = Object.freeze(new Set([
  'bond_claim', 'insurance_payout'
]));

export const FINANCING_CATEGORIES = Object.freeze(new Set([
  'loan_interest', 'loan_fees', 'bank_fees', 'borrowing_cost'
]));

export const CAPITAL_CATEGORIES = Object.freeze(new Set([
  'capital_improvement', 'depreciation', 'capital_works'
]));

export const ACQUISITION_CATEGORIES = Object.freeze(new Set([
  'stamp_duty', 'legal_conveyancing', 'buyer_agent', 'building_pest', 'acquisition_cost'
]));

export const REVIEW_REQUIRED_CATEGORIES = Object.freeze(new Set([
  'other_income', 'other_expense', 'accountant_review'
]));

/**
 * Historical financial-year summaries are intentionally stored in the existing
 * transactions collection with a marker in the already-persisted `month` field.
 * This keeps the backend schema unchanged while allowing an aggregate operating
 * expense to be distinguished from an ordinary "other expense" that still needs
 * accountant review.
 */
export const HISTORICAL_FY_SUMMARY_MARKERS = Object.freeze({
  income: 'FY_SUMMARY_INCOME',
  operatingExpenses: 'FY_SUMMARY_OPERATING_EXPENSES',
  loanInterest: 'FY_SUMMARY_LOAN_INTEREST'
});

export const getHistoricalFySummaryMarker = (transaction) => {
  const marker = String(transaction?.month || '').trim().toUpperCase();
  return Object.values(HISTORICAL_FY_SUMMARY_MARKERS).includes(marker) ? marker : '';
};

export const isHistoricalFySummaryTransaction = (transaction) => Boolean(getHistoricalFySummaryMarker(transaction));

/**
 * Classify one transaction for property-performance calculations.
 * NOI deliberately excludes financing, capital/acquisition, private, extraordinary
 * recovery and review-required categories. This prevents broad "not income" logic
 * from silently treating every outflow as an operating expense.
 *
 * The sole aggregate exception is an explicitly marked Historical FY Summary
 * operating-expense row. It is allowed into NOI because the user has deliberately
 * identified the annual amount as property operating expenses; its tax/category
 * breakdown remains subject to accountant review.
 */
export const classifyTransactionForPerformance = (transaction) => {
  const category = normaliseTransactionCategory(transaction?.category);
  const status = String(transaction?.status || '').trim().toLowerCase().replace(/\s+/g, '_');
  const amount = roundMoney(Math.abs(Number(transaction?.amount) || 0));
  const realised = status === 'paid';
  const historicalSummaryMarker = getHistoricalFySummaryMarker(transaction);

  let classification = 'review_required';
  if (historicalSummaryMarker === HISTORICAL_FY_SUMMARY_MARKERS.operatingExpenses) classification = 'operating_expense';
  else if (OPERATING_INCOME_CATEGORIES.has(category)) classification = 'operating_income';
  else if (OPERATING_EXPENSE_CATEGORIES.has(category)) classification = 'operating_expense';
  else if (NON_OPERATING_INCOME_CATEGORIES.has(category)) classification = 'non_operating_income';
  else if (FINANCING_CATEGORIES.has(category)) classification = 'financing';
  else if (CAPITAL_CATEGORIES.has(category)) classification = 'capital';
  else if (ACQUISITION_CATEGORIES.has(category)) classification = 'acquisition';
  else if (category === 'private_non_deductible') classification = 'private_excluded';
  else if (REVIEW_REQUIRED_CATEGORIES.has(category)) classification = 'review_required';

  return { category, status, amount, realised, classification, historicalSummaryMarker };
};

const transactionMatchesFilters = (transaction, filters = {}) => {
  if (filters.propertyIds?.length > 0 && !filters.propertyIds.map(String).includes(String(transaction?.propertyId || ''))) return false;
  if (filters.financialYear) {
    const transactionFy = transaction?.financialYear || (transaction?.date ? getAustralianFinancialYear(transaction.date) : '');
    if (transactionFy !== filters.financialYear) return false;
  }
  return true;
};

/**
 * Read realised rent evidence from Paid rent-schedule rows.
 * A schedule row is not cash evidence merely because it is due; only Paid rows
 * are eligible. paidToDate is used as the realised date when available,
 * otherwise the row due date is used for backward-compatible records.
 */
const getPaidRentScheduleEvidence = (filters = {}) => {
  const evidenceByProperty = new Map();
  const requestedPropertyIds = filters.propertyIds?.length > 0 ? filters.propertyIds.map(String) : null;
  const requestedFinancialYear = filters.financialYear && filters.financialYear !== 'All' ? filters.financialYear : null;

  demoRepository.getRentSchedules().forEach(row => {
    if (row?.archived === true) return;
    const status = String(row?.status || '').trim().toLowerCase().replace(/\s+/g, '_');
    const hasPaidEvidence = status === 'paid' || Boolean(row?.paidToDate);
    if (!hasPaidEvidence) return;

    const propertyId = String(row?.propertyId || row?.property || '');
    if (!propertyId) return;
    if (requestedPropertyIds && !requestedPropertyIds.includes(propertyId)) return;

    const realisedDate = row?.paidToDate || row?.dueDate;
    const rowFinancialYear = realisedDate
      ? getAustralianFinancialYear(realisedDate)
      : String(row?.financialYear || '');
    if (requestedFinancialYear && rowFinancialYear !== requestedFinancialYear) return;

    const grossRent = roundMoney(Math.abs(Number(row?.grossRentDue ?? row?.grossRent ?? 0) || 0));
    const agentFee = roundMoney(Math.abs(Number(row?.agentFeeAmount ?? row?.agentFee ?? 0) || 0));
    const otherDeductions = roundMoney(Math.abs(Number(row?.otherFees ?? row?.otherDeductions ?? 0) || 0));

    const evidence = evidenceByProperty.get(propertyId) || {
      propertyId,
      paidRowCount: 0,
      grossRent: 0,
      agentFee: 0,
      otherDeductions: 0
    };
    evidence.paidRowCount += 1;
    evidence.grossRent += grossRent;
    evidence.agentFee += agentFee;
    evidence.otherDeductions += otherDeductions;
    evidenceByProperty.set(propertyId,evidence);
  });

  evidenceByProperty.forEach(evidence => {
    evidence.grossRent = roundMoney(evidence.grossRent);
    evidence.agentFee = roundMoney(evidence.agentFee);
    evidence.otherDeductions = roundMoney(evidence.otherDeductions);
  });

  return evidenceByProperty;
};

/**
 * Build an auditable operating-performance view from realised cash evidence.
 *
 * Source precedence for rent:
 * 1) Paid rent transactions, when available for that property/FY.
 * 2) Paid rent-schedule rows only when no Paid rent transaction exists.
 *
 * Paid reimbursement transactions remain additional operating income.
 * Paid operating-expense transactions remain authoritative for expenses.
 * A known schedule agent fee is used only when no Paid agent-management-fee
 * transaction exists for that property/FY. Generic schedule "other deductions"
 * are not guessed into NOI because their accounting category is unknown.
 */
export const calculateOperatingPerformance = (filters = {}) => {
  const transactions = demoRepository.getTransactions().filter(t => transactionMatchesFilters(t, filters));
  const transactionEvidenceByProperty = new Map();
  const totals = {
    operatingIncome: 0,
    operatingExpenses: 0,
    nonOperatingIncome: 0,
    financingCosts: 0,
    capitalOutlays: 0,
    acquisitionOutlays: 0,
    privateExcluded: 0,
    reviewRequired: 0,
    paidTransactionCount: 0,
    excludedUnrealisedCount: 0,
    reviewRequiredCount: 0
  };

  transactions.forEach(transaction => {
    const classified = classifyTransactionForPerformance(transaction);
    if (!classified.realised) {
      totals.excludedUnrealisedCount += 1;
      return;
    }

    const propertyId = String(transaction?.propertyId || transaction?.property || '');
    const evidence = transactionEvidenceByProperty.get(propertyId) || {
      rentIncome: 0,
      reimbursementIncome: 0,
      agentManagementFee: 0
    };

    totals.paidTransactionCount += 1;
    switch (classified.classification) {
      case 'operating_income':
        totals.operatingIncome += classified.amount;
        if (classified.category === 'rent') evidence.rentIncome += classified.amount;
        if (classified.category === 'reimbursement') evidence.reimbursementIncome += classified.amount;
        break;
      case 'operating_expense':
        totals.operatingExpenses += classified.amount;
        if (classified.category === 'agent_management_fee') evidence.agentManagementFee += classified.amount;
        break;
      case 'non_operating_income': totals.nonOperatingIncome += classified.amount; break;
      case 'financing': totals.financingCosts += classified.amount; break;
      case 'capital': totals.capitalOutlays += classified.amount; break;
      case 'acquisition': totals.acquisitionOutlays += classified.amount; break;
      case 'private_excluded': totals.privateExcluded += classified.amount; break;
      default:
        totals.reviewRequired += classified.amount;
        totals.reviewRequiredCount += 1;
        break;
    }
    transactionEvidenceByProperty.set(propertyId,evidence);
  });

  const scheduleEvidenceByProperty = getPaidRentScheduleEvidence(filters);
  let scheduleRentUsed = 0;
  let scheduleAgentFeeUsed = 0;
  let scheduleOtherDeductions = 0;
  let paidRentScheduleCount = 0;
  let scheduleSourcePropertyCount = 0;

  scheduleEvidenceByProperty.forEach((scheduleEvidence,propertyId) => {
    if (scheduleEvidence.paidRowCount <= 0) return;
    const transactionEvidence = transactionEvidenceByProperty.get(propertyId) || {
      rentIncome: 0,
      reimbursementIncome: 0,
      agentManagementFee: 0
    };

    // Transactions remain the primary realised-rent ledger. Paid schedule rent
    // is a fallback only when no Paid rent transaction exists for this property/FY.
    // This prevents both double counting and a partially maintained schedule from
    // replacing a more complete transaction ledger.
    if (transactionEvidence.rentIncome <= 0 && scheduleEvidence.grossRent > 0) {
      totals.operatingIncome += scheduleEvidence.grossRent;
      scheduleRentUsed += scheduleEvidence.grossRent;
      paidRentScheduleCount += scheduleEvidence.paidRowCount;
      scheduleSourcePropertyCount += 1;
    }

    // Agent fees are categorically known. Use schedule fees only where an
    // explicit Paid management-fee transaction does not already exist.
    if (transactionEvidence.agentManagementFee <= 0 && scheduleEvidence.agentFee > 0) {
      totals.operatingExpenses += scheduleEvidence.agentFee;
      scheduleAgentFeeUsed += scheduleEvidence.agentFee;
    }

    // Other schedule deductions lack a category, so do not guess them into NOI.
    scheduleOtherDeductions += scheduleEvidence.otherDeductions;
  });

  Object.keys(totals).forEach(key => {
    if (!key.endsWith('Count')) totals[key] = roundMoney(totals[key]);
  });
  totals.netOperatingIncome = roundMoney(totals.operatingIncome - totals.operatingExpenses);
  totals.scheduleRentUsed = roundMoney(scheduleRentUsed);
  totals.scheduleAgentFeeUsed = roundMoney(scheduleAgentFeeUsed);
  totals.scheduleOtherDeductions = roundMoney(scheduleOtherDeductions);
  totals.paidRentScheduleCount = paidRentScheduleCount;
  totals.scheduleSourcePropertyCount = scheduleSourcePropertyCount;

  const paidRentTransactionTotal = roundMoney(
    [...transactionEvidenceByProperty.values()].reduce((sum,evidence) => sum + evidence.rentIncome,0)
  );
  totals.rentIncomeSource = scheduleSourcePropertyCount > 0
    ? paidRentTransactionTotal > 0 ? 'paid_rent_schedule_with_transaction_reconciliation' : 'paid_rent_schedule'
    : paidRentTransactionTotal > 0 ? 'paid_transactions' : 'none';
  totals.agentFeeSource = scheduleAgentFeeUsed > 0 ? 'paid_rent_schedule' : 'paid_transactions_or_none';

  return totals;
};

export const getPropertyOperatingPerformance = (propertyId, financialYear = getAustralianFinancialYear(new Date())) => {
  if (!propertyId) return null;
  const performance = calculateOperatingPerformance({ propertyIds: [propertyId], financialYear });
  const property = demoRepository.getPropertyById(propertyId);
  const rentPosition = getPropertyAnnualRentPosition(propertyId);
  const marketValue = roundMoney(Number(property?.currentMarketValue) || 0);
  const grossYield = calculateGrossYield(rentPosition.annualRent, marketValue);

  return {
    propertyId,
    financialYear,
    expectedAnnualRent: rentPosition.annualRent,
    expectedRentSource: rentPosition.source,
    marketValue,
    grossYield,
    ...performance,
    calculationHelp: {
      expectedRent: `${formatCurrencyAUD(rentPosition.annualRent)} expected annual rent from ${rentPosition.source.replace(/_/g, ' ')}.`,
      grossYield: `${formatCurrencyAUD(rentPosition.annualRent)} ÷ ${formatCurrencyAUD(marketValue)} = ${formatPercentage(grossYield)} gross yield.`,
      operatingIncome: performance.scheduleRentUsed > 0
        ? `${formatCurrencyAUD(performance.operatingIncome)} realised operating income. ${formatCurrencyAUD(performance.scheduleRentUsed)} rent is sourced from ${performance.paidRentScheduleCount} Paid rent-schedule row(s) because no Paid rent transaction exists for that property/FY. Paid reimbursements remain additional income.`
        : `${formatCurrencyAUD(performance.operatingIncome)} from Paid rent and reimbursement transactions in FY ${financialYear}.`,
      operatingExpenses: performance.scheduleAgentFeeUsed > 0
        ? `${formatCurrencyAUD(performance.operatingExpenses)} realised operating expenses. ${formatCurrencyAUD(performance.scheduleAgentFeeUsed)} of agent-management fees is sourced from Paid rent-schedule rows where no Paid management-fee transaction exists; other Paid operating-cost transactions remain included.`
        : `${formatCurrencyAUD(performance.operatingExpenses)} from Paid operating-cost transactions; financing, capital, acquisition and private items are excluded.`,
      netOperatingIncome: `${formatCurrencyAUD(performance.operatingIncome)} − ${formatCurrencyAUD(performance.operatingExpenses)} = ${formatCurrencyAUD(performance.netOperatingIncome)} NOI.`,
      reviewRequired: performance.scheduleOtherDeductions > 0
        ? `${formatCurrencyAUD(performance.scheduleOtherDeductions)} of generic deductions exists on Paid rent-schedule rows but is excluded from NOI because no accounting category is recorded. Categorise those costs as transactions if they should be included in operating expenses.`
        : performance.reviewRequiredCount > 0
          ? `${performance.reviewRequiredCount} Paid transaction(s), totalling ${formatCurrencyAUD(performance.reviewRequired)}, are excluded from NOI until categorised more specifically.`
          : 'No Paid transaction or uncategorised Paid rent-schedule deduction requires classification review for this period.'
    }
  };
};

/**
 * Calculate total income
 */
export const calculateIncome = (filters = {}) => {
  let txns = demoRepository.getIncomeTransactions();
  if (filters.propertyIds?.length > 0) txns = txns.filter(t => filters.propertyIds.includes(t.propertyId));
  if (filters.financialYear) txns = txns.filter(t => t.financialYear === filters.financialYear);
  return roundMoney(txns.reduce((sum, t) => sum + Math.abs(t.amount), 0));
};

/**
 * Calculate total expenses
 */
export const calculateExpenses = (filters = {}) => {
  let txns = demoRepository.getExpenseTransactions();
  if (filters.propertyIds?.length > 0) txns = txns.filter(t => filters.propertyIds.includes(t.propertyId));
  if (filters.financialYear) txns = txns.filter(t => t.financialYear === filters.financialYear);
  return roundMoney(txns.reduce((sum, t) => sum + Math.abs(t.amount), 0));
};

/**
 * Calculate insurance expenses
 */
export const calculateInsuranceExpenses = (filters = {}) => {
  let policies = demoRepository.getActiveInsurancePolicies();
  if (filters.propertyIds?.length > 0) policies = policies.filter(p => filters.propertyIds.includes(p.propertyId));
  return roundMoney(policies.reduce((sum, p) => sum + (p.premium || 0), 0));
};

/**
 * Calculate net operating cash flow
 */
export const calculateNetOperatingCashFlow = (income, expenses) => roundMoney(income - expenses);

/**
 * Calculate cash flow after loan payments
 */
export const calculateCashFlowAfterLoanPayments = (income, expenses, loans) => roundMoney(income - expenses - loans);

/**
 * Calculate equity (value - loan)
 */
export const calculateEquity = (value, loan) => roundMoney(value - loan);

/**
 * Calculate LVR (loan / value)
 */
export const calculateLVR = (loan, value) => value === 0 ? 0 : roundPercent(loan / value);

/**
 * Calculate gross yield (rent / value)
 */
export const calculateGrossYield = (rent, value) => value === 0 ? 0 : roundPercent(rent / value);

/**
 * Calculate net yield ((rent - exp) / value)
 */
export const calculateNetYield = (rent, exp, value) => value === 0 ? 0 : roundPercent((rent - exp) / value);

/**
 * Calculate open item age in days
 */
export const calculateOpenItemAge = (date) => Math.floor((Date.now() - new Date(date).getTime()) / 86400000);

/**
 * Resolve the authoritative current loan position for one property.
 * Source precedence:
 * 1) active linked loan records (currentLoanBalance, then legacy currentBalance)
 * 2) property financial metadata fallback when no active linked loan exists
 * Linked-loan and property-level balances are never added together.
 */
export const getPropertyLoanPosition = (propertyId) => {
  if (!propertyId) return { balance: 0, source: 'none', loanCount: 0 };

  const excludedStatuses = new Set(['closed', 'refinanced', 'archived']);
  const linkedLoans = demoRepository.getAllLoans().filter(loan => {
    if (String(loan.propertyId || '') !== String(propertyId)) return false;
    const status = String(loan.status || '').trim().toLowerCase();
    return !excludedStatuses.has(status);
  });

  const readLoanBalance = (loan) => {
    const preferred = loan?.currentLoanBalance;
    if (preferred !== null && preferred !== undefined && preferred !== '' && Number.isFinite(Number(preferred))) {
      return Number(preferred);
    }
    const legacy = loan?.currentBalance;
    return legacy !== null && legacy !== undefined && legacy !== '' && Number.isFinite(Number(legacy)) ? Number(legacy) : 0;
  };

  if (linkedLoans.length > 0) {
    return {
      balance: roundMoney(linkedLoans.reduce((sum, loan) => sum + readLoanBalance(loan), 0)),
      source: 'linked_loans',
      loanCount: linkedLoans.length
    };
  }

  const prop = demoRepository.getPropertyById(propertyId);
  const fallback = prop?.metadata?.financial?.currentLoanBalance ?? prop?.currentLoanBalance;
  const hasFallback = fallback !== null && fallback !== undefined && fallback !== '' && Number.isFinite(Number(fallback));
  return {
    balance: hasFallback ? roundMoney(Number(fallback)) : 0,
    source: hasFallback ? 'property_financial' : 'none',
    loanCount: 0
  };
};

/**
 * Calculate total loan balance. For an individual property, use the
 * authoritative source precedence defined in getPropertyLoanPosition.
 */
export const calculateTotalLoanBalance = (propertyId = null) => {
  if (propertyId) return getPropertyLoanPosition(propertyId).balance;

  const excludedStatuses = new Set(['closed', 'refinanced', 'archived']);
  const loans = demoRepository.getAllLoans().filter(loan => !excludedStatuses.has(String(loan.status || '').trim().toLowerCase()));
  return roundMoney(loans.reduce((sum, loan) => {
    const preferred = loan?.currentLoanBalance;
    if (preferred !== null && preferred !== undefined && preferred !== '' && Number.isFinite(Number(preferred))) {
      return sum + Number(preferred);
    }
    const legacy = loan?.currentBalance;
    return sum + (legacy !== null && legacy !== undefined && legacy !== '' && Number.isFinite(Number(legacy)) ? Number(legacy) : 0);
  }, 0));
};

/**
 * Calculate total monthly loan payments
 */
export const calculateTotalMonthlyLoanPayments = (propertyId = null) => {
  let loans = demoRepository.getAllLoans();
  if (propertyId) loans = loans.filter(l => l.propertyId === propertyId);
  return roundMoney(loans.reduce((sum, l) => sum + (l.monthlyPayment || 0), 0));
};

/**
 * Formula-driven monthly loan installment for display/reporting.
 * Interest-only loans use monthly interest. Principal & interest loans use the
 * standard amortisation formula and require a term. This helper is additive
 * only and does not alter existing cash-flow/accounting calculations.
 */
export const calculateLoanMonthlyInstallment = ({
  balance = 0,
  interestRate = 0,
  repaymentType = '',
  termYears = 0,
  loanStartDate = null
} = {}) => {
  const principal = Number(balance);
  const annualRate = Number(interestRate);
  const years = Number(termYears);
  if (!Number.isFinite(principal) || principal <= 0) return 0;
  if (!Number.isFinite(annualRate) || annualRate < 0) return null;

  const repayment = String(repaymentType || '').trim().toLowerCase().replace(/\s+/g, '_');
  const monthlyRate = annualRate / 100 / 12;

  if (repayment === 'interest_only' || repayment === 'line_of_credit') {
    return roundMoney(principal * monthlyRate);
  }

  if (repayment === 'principal_&_interest' || repayment === 'principal_and_interest' || repayment === 'principal_interest') {
    if (!Number.isFinite(years) || years <= 0) return null;
    const totalMonths = Math.round(years * 12);
    if (totalMonths <= 0) return null;
    let elapsedMonths = 0;
    if (loanStartDate) {
      const start = new Date(loanStartDate);
      const now = new Date();
      if (!Number.isNaN(start.getTime()) && start.getTime() < now.getTime()) {
        elapsedMonths = Math.max(0, ((now.getFullYear() - start.getFullYear()) * 12) + (now.getMonth() - start.getMonth()));
        if (now.getDate() < start.getDate()) elapsedMonths = Math.max(0, elapsedMonths - 1);
      }
    }
    const months = Math.max(1, totalMonths - elapsedMonths);
    if (monthlyRate === 0) return roundMoney(principal / months);
    const factor = Math.pow(1 + monthlyRate, months);
    return roundMoney(principal * monthlyRate * factor / (factor - 1));
  }

  return null;
};

/**
 * Resolve display-only loan metrics for one property without double counting.
 * Active linked loan records remain authoritative when present; otherwise the
 * property's existing financial metadata is used as a fallback.
 */
export const getPropertyLoanMetrics = (propertyId) => {
  const property = demoRepository.getPropertyById(propertyId);
  const financial = property?.metadata?.financial || {};
  const linkedLoans = demoRepository.getAllLoans().filter(loan => {
    if (String(loan.propertyId || '') !== String(propertyId || '')) return false;
    if (loan?.archived === true) return false;
    const status = String(loan.status || '').trim().toLowerCase();
    return status === 'active' || status === '';
  });

  const readBalance = (loan) => {
    const value = loan?.currentLoanBalance ?? loan?.currentBalance;
    return value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value)) ? Number(value) : 0;
  };
  const readRate = (loan) => {
    const direct = loan?.interestRate;
    if (direct !== null && direct !== undefined && direct !== '' && Number.isFinite(Number(direct)) && Number(direct) > 0) {
      return Number(direct);
    }
    const fallback = financial?.interestRate;
    return fallback !== null && fallback !== undefined && fallback !== '' && Number.isFinite(Number(fallback)) && Number(fallback) > 0 ? Number(fallback) : null;
  };
  const readTerm = (loan) => {
    const value = loan?.loanTermYears ?? loan?.termYears ?? loan?.remainingTermYears ?? loan?.metadata?.loanTermYears ?? loan?.metadata?.termYears ?? financial?.loanTermYears;
    return value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value)) ? Number(value) : 0;
  };
  const readRepaymentType = (loan) => loan?.repaymentType || financial?.repaymentType || '';
  const readStartDate = (loan) => loan?.loanStartDate || loan?.establishmentDate || financial?.loanStartDate || null;

  const records = linkedLoans.length > 0 ? linkedLoans.map(loan => ({
    balance: readBalance(loan),
    interestRate: readRate(loan),
    repaymentType: readRepaymentType(loan),
    termYears: readTerm(loan),
    loanStartDate: readStartDate(loan)
  })) : [{
    balance: Number(financial?.currentLoanBalance ?? property?.currentLoanBalance ?? 0) || 0,
    interestRate: financial?.interestRate !== null && financial?.interestRate !== undefined && financial?.interestRate !== '' && Number.isFinite(Number(financial.interestRate)) && Number(financial.interestRate) > 0 ? Number(financial.interestRate) : null,
    repaymentType: financial?.repaymentType || property?.repaymentType || '',
    termYears: Number(financial?.loanTermYears ?? property?.loanTermYears ?? 0) || 0,
    loanStartDate: financial?.loanStartDate || property?.loanStartDate || null
  }];

  const balance = roundMoney(records.reduce((sum, record) => sum + Math.max(0, Number(record.balance) || 0), 0));
  const ratedBalance = records.reduce((sum, record) => record.interestRate === null ? sum : sum + Math.max(0, Number(record.balance) || 0), 0);
  const weightedInterest = records.reduce((sum, record) => (
    record.interestRate === null ? sum : sum + (Math.max(0, Number(record.balance) || 0) * Number(record.interestRate))
  ), 0);
  const interestRate = ratedBalance > 0 ? weightedInterest / ratedBalance : null;

  let monthlyInstallment = 0;
  let incompleteLoanCount = 0;
  records.forEach(record => {
    if ((Number(record.balance) || 0) <= 0) return;
    const installment = calculateLoanMonthlyInstallment(record);
    if (installment === null) incompleteLoanCount += 1;
    else monthlyInstallment += installment;
  });

  return {
    balance,
    interestRate: interestRate === null ? null : Math.round(interestRate * 1000) / 1000,
    monthlyInstallment: roundMoney(monthlyInstallment),
    installmentComplete: incompleteLoanCount === 0,
    incompleteLoanCount,
    loanCount: linkedLoans.length || (balance > 0 ? 1 : 0),
    source: linkedLoans.length > 0 ? 'linked_loans' : (balance > 0 ? 'property_financial' : 'none')
  };
};

/**
 * Resolve the current anticipated rent position for one property from the
 * existing rent schedule. This is a forward-looking display calculation only.
 * It does not replace recorded transaction/accounting performance.
 */
export const getPropertyAnticipatedRentMetrics = (propertyId, asOf = Date.now()) => {
  const firstRecordedNumber = (...values) => {
    for (const value of values) {
      if (value === null || value === undefined || value === '') continue;
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
    return null;
  };

  const getPaymentPeriodsPerYear = (frequency, calculation = {}) => {
    const explicit = Number(calculation.paymentPeriodsPerYear);
    if (Number.isFinite(explicit) && explicit > 0) return explicit;
    const key = String(frequency || 'weekly').trim().toLowerCase();
    return getRentBasisPeriodsPerYear(key);
  };

  const getScheduleRowForDate = (rows = []) => {
    const usableRows = rows
      .filter(row => !row?.archived && String(row?.status || '').trim().toLowerCase() !== 'archived')
      .sort((a, b) => (new Date(a?.dueDate || 0).getTime() || 0) - (new Date(b?.dueDate || 0).getTime() || 0));
    if (!usableRows.length) return null;
    const target = new Date(asOf).getTime();
    const containing = usableRows.find(row => {
      const start = new Date(row?.periodStart ?? row?.dueDate ?? 0).getTime();
      const end = new Date(row?.periodEnd ?? row?.dueDate ?? 0).getTime();
      return Number.isFinite(start) && Number.isFinite(end) && start <= target && target <= end;
    });
    if (containing) return containing;
    const prior = usableRows.filter(row => {
      const dueDate = new Date(row?.dueDate || 0).getTime();
      return Number.isFinite(dueDate) && dueDate <= target;
    });
    return prior.length ? prior[prior.length - 1] : usableRows[0];
  };

  const property = demoRepository.getPropertyById(propertyId);
  const recordedManagementFee = property?.managementFeePercent ?? property?.metadata?.agent?.managementFeePercent ?? 0;
  const propertyManagementFeePercent = Number.isFinite(Number(recordedManagementFee)) ? Number(recordedManagementFee) : 0;

  const readMonthly = (row, lease) => {
    const annualisation = getRentAnnualisation(lease?.weeklyRent ?? lease?.rentAmount ?? 0, lease?.rentBasis || 'weekly');
    if (!annualisation.supported) return null;

    const calculation = row?.metadata?.rentCalculation || {};
    const frequency = calculation.paymentFrequency || row?.paymentFrequency || lease?.paymentFrequency || 'weekly';
    const periodsPerYear = getPaymentPeriodsPerYear(frequency, calculation);
    if (!Number.isFinite(periodsPerYear) || periodsPerYear <= 0) return null;

    const monthlyGrossRent = roundMoney(annualisation.annualRent / 12);
    const explicitFeePercent = firstRecordedNumber(
      row?.agentFeePercent,
      calculation.agentFeePercent,
      propertyManagementFeePercent
    );
    const feePercent = explicitFeePercent === null ? 0 : Math.max(0, explicitFeePercent);
    const monthlyAgentFee = roundMoney(monthlyGrossRent * (feePercent / 100));

    let otherPerPeriod = firstRecordedNumber(row?.otherFees, row?.otherDeductions, calculation.otherDeductions);
    if (otherPerPeriod === null) {
      const recordedTotal = firstRecordedNumber(row?.totalDeductions, calculation.totalDeductions);
      const recordedAgent = firstRecordedNumber(row?.agentFeeAmount, row?.agentFee, calculation.agentFeeAmount);
      if (recordedTotal !== null && recordedAgent !== null) otherPerPeriod = roundMoney(recordedTotal - recordedAgent);
    }
    if (otherPerPeriod === null) otherPerPeriod = 0;
    const monthlyOtherDeductions = roundMoney((otherPerPeriod * periodsPerYear) / 12);
    const monthlyTotalDeductions = roundMoney(monthlyAgentFee + monthlyOtherDeductions);
    const monthlyNetRent = roundMoney(monthlyGrossRent - monthlyTotalDeductions);

    return {
      monthlyGrossRent,
      monthlyAgentFee,
      monthlyOtherDeductions,
      monthlyTotalDeductions,
      monthlyNetRent,
      rentComplete: true,
      feeComplete: true
    };
  };

  const currentLeases = typeof demoRepository.getCurrentLeasesByProperty === 'function'
    ? demoRepository.getCurrentLeasesByProperty(propertyId, asOf)
    : [];
  const schedules = demoRepository.getRentSchedules().filter(row => (
    String(row?.propertyId || row?.property || '') === String(propertyId || '') &&
    !row?.archived &&
    String(row?.status || '').trim().toLowerCase() !== 'archived'
  ));

  const totals = {
    monthlyGrossRent: 0,
    monthlyFeeGrossRent: 0,
    monthlyAgentFee: 0,
    monthlyOtherDeductions: 0,
    monthlyTotalDeductions: 0,
    monthlyNetRent: 0,
    rentMissingCount: 0,
    feeMissingCount: 0
  };

  currentLeases.forEach(lease => {
    const row = getScheduleRowForDate(
      schedules.filter(schedule => String(schedule?.leaseId || schedule?.lease || '') === String(lease.id))
    );
    const monthly = readMonthly(row, lease);
    if (!monthly?.rentComplete) {
      totals.rentMissingCount += 1;
      return;
    }
    totals.monthlyGrossRent += monthly.monthlyGrossRent;
    totals.monthlyOtherDeductions += monthly.monthlyOtherDeductions;
    totals.monthlyTotalDeductions += monthly.monthlyTotalDeductions;
    totals.monthlyNetRent += monthly.monthlyNetRent;
    if (!monthly.feeComplete) {
      totals.feeMissingCount += 1;
    } else {
      totals.monthlyFeeGrossRent += monthly.monthlyGrossRent;
      totals.monthlyAgentFee += monthly.monthlyAgentFee;
    }
  });

  const monthlyGrossRent = roundMoney(totals.monthlyGrossRent);
  const monthlyNetRent = roundMoney(totals.monthlyNetRent);
  const effectiveAgentFeePercent = totals.feeMissingCount === 0 && totals.monthlyFeeGrossRent > 0
    ? roundMoney((totals.monthlyAgentFee / totals.monthlyFeeGrossRent) * 100)
    : null;

  return {
    monthlyGrossRent,
    monthlyAgentFee: roundMoney(totals.monthlyAgentFee),
    monthlyOtherDeductions: roundMoney(totals.monthlyOtherDeductions),
    monthlyTotalDeductions: roundMoney(totals.monthlyTotalDeductions),
    monthlyNetRent,
    annualGrossRent: roundMoney(monthlyGrossRent * 12),
    annualNetRent: roundMoney(monthlyNetRent * 12),
    monthlyFeeGrossRent: roundMoney(totals.monthlyFeeGrossRent),
    effectiveAgentFeePercent,
    rentMissingCount: totals.rentMissingCount,
    feeMissingCount: totals.feeMissingCount,
    rentComplete: totals.rentMissingCount === 0 && currentLeases.length > 0,
    feeComplete: totals.feeMissingCount === 0 && currentLeases.length > 0,
    source: currentLeases.length > 0 ? 'current_contractual_leases' : 'none',
    recordCount: currentLeases.length,
    asOf
  };
};

/**
 * Current high-level investment cash-flow projection.
 * Formula: anticipated monthly net rent minus formula-driven monthly installment.
 * Costs absent from the rent schedule are intentionally not invented and must be
 * disclosed by the UI as excluded from this indicative figure.
 */
export const getPropertyIndicativeCashFlowPosition = (propertyId, asOf = Date.now()) => {
  const rent = getPropertyAnticipatedRentMetrics(propertyId, asOf);
  const loan = getPropertyLoanMetrics(propertyId);
  const complete = Boolean(rent?.rentComplete) && Boolean(loan?.installmentComplete);
  const monthlyCashFlow = complete ? roundMoney(rent.monthlyNetRent - loan.monthlyInstallment) : null;
  const annualCashFlow = monthlyCashFlow === null ? null : roundMoney(monthlyCashFlow * 12);
  const classification = monthlyCashFlow === null
    ? 'incomplete'
    : monthlyCashFlow > 0.004
      ? 'positive'
      : monthlyCashFlow < -0.004
        ? 'negative'
        : 'neutral';
  return {
    rent,
    loan,
    complete,
    monthlyCashFlow,
    annualCashFlow,
    classification
  };
};

export const getPropertyNextContractedPosition = (propertyId, asOf = Date.now()) => {
  const propertyLeases = demoRepository.getAllLeases().filter(lease => (
    String(lease?.propertyId || lease?.property || '') === String(propertyId || '')
  ));
  const effectiveDate = getNextContractedChangeDate(propertyLeases, asOf);
  if (effectiveDate === null) return null;

  const position = getPropertyIndicativeCashFlowPosition(propertyId, effectiveDate);
  const current = getPropertyIndicativeCashFlowPosition(propertyId, asOf);
  return {
    effectiveDate,
    ...position,
    changedGrossRent: position.rent?.monthlyGrossRent !== current.rent?.monthlyGrossRent,
    changedNetRent: position.rent?.monthlyNetRent !== current.rent?.monthlyNetRent,
    changedCashFlow: position.monthlyCashFlow !== current.monthlyCashFlow
  };
};

/**
 * Aggregate formula-driven loan metrics across a supplied property set.
 */
export const getPortfolioLoanMetrics = (propertyIds = null) => {
  const properties = demoRepository.getAllProperties().filter(property => (
    !propertyIds || propertyIds.map(String).includes(String(property.id))
  ));
  const metrics = properties.map(property => getPropertyLoanMetrics(property.id));
  const totalLoanBalance = roundMoney(metrics.reduce((sum, metric) => sum + metric.balance, 0));
  const totalMonthlyInstallment = roundMoney(metrics.reduce((sum, metric) => sum + metric.monthlyInstallment, 0));
  const ratedBalance = metrics.reduce((sum, metric) => metric.interestRate === null ? sum : sum + metric.balance, 0);
  const weightedRate = metrics.reduce((sum, metric) => metric.interestRate === null ? sum : sum + (metric.balance * metric.interestRate), 0);
  return {
    totalLoanBalance,
    totalMonthlyInstallment,
    weightedInterestRate: ratedBalance > 0 ? Math.round((weightedRate / ratedBalance) * 1000) / 1000 : null,
    installmentComplete: metrics.every(metric => metric.installmentComplete),
    incompleteLoanCount: metrics.reduce((sum, metric) => sum + metric.incompleteLoanCount, 0)
  };
};

/**
 * Backwards-compatible weekly-rent helper.
 */
export const calculateAnnualRent = (weeklyRent) => calculateAnnualizedRent(weeklyRent, 'weekly');

/**
 * Resolve annual expected rent for one property from the most specific active source.
 * Active leases are authoritative when present. Occupied-space rent is a fallback
 * only when the property has no active leases, preventing lease + space double count.
 */
export const getPropertyAnnualRentPosition = (propertyId, asOf = Date.now()) => {
  if (!propertyId) {
    return { annualRent: 0, source: 'none', recordCount: 0, unsupportedCount: 0, complete: true };
  }

  const currentLeases = typeof demoRepository.getCurrentLeasesByProperty === 'function'
    ? demoRepository.getCurrentLeasesByProperty(propertyId, asOf)
    : [];

  let annualRent = 0;
  let unsupportedCount = 0;
  currentLeases.forEach(lease => {
    const annualisation = getRentAnnualisation(lease?.weeklyRent ?? lease?.rentAmount ?? 0, lease?.rentBasis || 'weekly');
    if (!annualisation.supported) {
      unsupportedCount += 1;
      return;
    }
    annualRent += annualisation.annualRent;
  });

  return {
    annualRent: roundMoney(annualRent),
    source: currentLeases.length > 0 ? 'current_contractual_leases' : 'none',
    recordCount: currentLeases.length,
    unsupportedCount,
    complete: unsupportedCount === 0
  };
};

/**
 * Calculate occupancy rate
 */
export const calculateOccupancyRate = (spaces) => {
  if (!spaces?.length) return 0;
  return roundPercent(spaces.filter(s => s.status === 'Occupied').length / spaces.length);
};

/**
 * Calculate portfolio annual rent
 */
export const calculatePortfolioAnnualRent = (propertyIds = null, asOf = Date.now()) => {
  const properties = demoRepository.getAllProperties().filter(property => (
    String(property?.status || '').trim().toLowerCase() !== 'archived' &&
    (!propertyIds || propertyIds.map(String).includes(String(property.id)))
  ));
  return roundMoney(properties.reduce((sum, property) => (
    sum + getPropertyAnnualRentPosition(property.id, asOf).annualRent
  ), 0));
};

/**
 * Calculate transaction totals by category
 */
export const calculateTransactionTotalsByCategory = (propertyId = null, financialYear = null) => {
  let transactions = demoRepository.getTransactions();
  if (propertyId) transactions = transactions.filter(t => t.propertyId === propertyId);
  if (financialYear) transactions = transactions.filter(t => t.financialYear === financialYear);
  const categories = {};
  transactions.forEach(t => {
    if (!categories[t.category]) {
      categories[t.category] = { income: 0, expense: 0, count: 0 };
    }
    categories[t.category].count++;
    if (t.amount > 0) {
      categories[t.category].income += t.amount;
    } else {
      categories[t.category].expense += Math.abs(t.amount);
    }
  });
  return categories;
};

/**
 * Calculate property insurance status
 */
export const calculatePropertyInsuranceStatus = (propertyId) => {
  const policies = demoRepository.getInsuranceByProperty(propertyId);
  const activePolicies = policies.filter(p => p.status === 'active');
  const expiringSoon = activePolicies.filter(p => {
    const days = calculateDaysToExpiry(p.expiryDate);
    return days > 0 && days <= 30;
  });
  const expired = activePolicies.filter(p => calculateDaysToExpiry(p.expiryDate) < 0);
  return {
    totalPolicies: policies.length,
    activePolicies: activePolicies.length,
    expiringSoon: expiringSoon.length,
    expired: expired.length,
    totalPremium: activePolicies.reduce((sum, p) => sum + p.premium, 0),
    status: expired.length > 0 ? 'expired' : expiringSoon.length > 0 ? 'expiring' : activePolicies.length > 0 ? 'covered' : 'uninsured'
  };
};

/**
 * Get comprehensive property summary with all calculations verified
 */
export const getPropertySummary = (propertyId) => {
  const prop = demoRepository.getPropertyById(propertyId);
  if (!prop) return null;
  const spaces = demoRepository.getSpacesByProperty(propertyId);
  const totalAcquisitionCost = calculatePropertyAcquisitionCost(prop);
  const loanPosition = getPropertyLoanPosition(propertyId);
  const totalLoanBalance = loanPosition.balance;
  const totalMonthlyLoanPayments = calculateTotalMonthlyLoanPayments(propertyId);
  const equity = calculateEquity(prop.currentMarketValue || 0, totalLoanBalance);
  const lvr = calculateLVR(totalLoanBalance, prop.currentMarketValue || 0);
  const gainLossAcquisition = roundMoney((prop.currentMarketValue || 0) - totalAcquisitionCost);
  const currentLeases = typeof demoRepository.getCurrentLeasesByProperty === 'function'
    ? demoRepository.getCurrentLeasesByProperty(propertyId)
    : [];
  const effectiveSpacesCount = spaces.length || (currentLeases.length > 0 ? 1 : 0);
  const occupiedSpaces = spaces.length
    ? spaces.filter(s => String(s.status || '').trim().toLowerCase() === 'occupied').length
    : currentLeases.length > 0 ? 1 : 0;
  const occupancyRate = effectiveSpacesCount ? occupiedSpaces / effectiveSpacesCount : 0;
  const rentPosition = getPropertyAnnualRentPosition(propertyId);
  const annualRent = rentPosition.annualRent;
  const currentFinancialYear = getAustralianFinancialYear(new Date());
  const operatingPerformance = getPropertyOperatingPerformance(propertyId, currentFinancialYear);
  const income = calculateIncome({ propertyIds: [propertyId] });
  const expenses = calculateExpenses({ propertyIds: [propertyId] });
  const insuranceExpenses = calculateInsuranceExpenses({ propertyIds: [propertyId] });
  const netCashFlow = calculateCashFlowAfterLoanPayments(income, expenses + insuranceExpenses, totalMonthlyLoanPayments * 12);
  const openItemsCount = demoRepository.getOpenItemsByProperty(propertyId).filter(i => i.status !== 'Resolved' && i.status !== 'Archived').length;
  const insuranceStatus = calculatePropertyInsuranceStatus(propertyId);
  return {
    totalAcquisitionCost,
    totalLoanBalance,
    loanBalanceSource: loanPosition.source,
    activeLoanCount: loanPosition.loanCount,
    equity,
    lvr,
    gainLossAcquisition,
    occupancyRate,
    annualRent,
    annualRentSource: rentPosition.source,
    annualRentRecordCount: rentPosition.recordCount,
    annualRentUnsupportedCount: rentPosition.unsupportedCount,
    annualRentComplete: rentPosition.complete,
    currentFinancialYear,
    actualOperatingIncome: operatingPerformance?.operatingIncome || 0,
    actualOperatingExpenses: operatingPerformance?.operatingExpenses || 0,
    netOperatingIncome: operatingPerformance?.netOperatingIncome || 0,
    grossYield: operatingPerformance?.grossYield || 0,
    nonOperatingIncome: operatingPerformance?.nonOperatingIncome || 0,
    financingCosts: operatingPerformance?.financingCosts || 0,
    capitalOutlays: operatingPerformance?.capitalOutlays || 0,
    acquisitionOutlays: operatingPerformance?.acquisitionOutlays || 0,
    performanceReviewRequired: operatingPerformance?.reviewRequired || 0,
    performanceReviewRequiredCount: operatingPerformance?.reviewRequiredCount || 0,
    operatingCalculationHelp: operatingPerformance?.calculationHelp || null,
    netCashFlow,
    openItemsCount,
    spacesCount: effectiveSpacesCount,
    occupiedSpaces,
    currentLeaseCount: currentLeases.length,
    occupancyStatus: occupiedSpaces > 0 ? 'Occupied' : 'Vacant',
    insuranceStatus
  };
};

/**
 * Get comprehensive portfolio summary with all calculations verified
 */
export const getPortfolioSummary = (propertyIds = null) => {
  let properties = demoRepository.getAllProperties().filter(p => p.status !== 'Archived');
  if (propertyIds) {
    properties = properties.filter(p => propertyIds.includes(p.id));
  }
  const occupancySummaries = properties.map(property => getPropertySummary(property.id)).filter(Boolean);
  const spaces = occupancySummaries.reduce((sum, summary) => sum + (Number(summary.spacesCount) || 0), 0);
  const occupiedSpaces = occupancySummaries.reduce((sum, summary) => sum + (Number(summary.occupiedSpaces) || 0), 0);
  const totalMarketValue = calculatePortfolioCurrentMarketValue(properties);
  const totalAcquisitionCost = calculatePortfolioAcquisitionValue(properties);
  const totalLoanBalance = calculateTotalLoanBalance(propertyIds?.length === 1 ? propertyIds[0] : null);
  const annualRent = calculatePortfolioAnnualRent(properties.map(property => property.id));
  const annualExpenses = calculateExpenses({ financialYear: getAustralianFinancialYear(new Date()) });
  const annualInsurance = calculateInsuranceExpenses();
  const monthlyLoans = calculateTotalMonthlyLoanPayments(propertyIds?.length === 1 ? propertyIds[0] : null);
  return {
    totalProperties: properties.length,
    totalSpaces: spaces,
    occupiedSpaces,
    vacancyRate: roundPercent(1 - (occupiedSpaces / (spaces || 1))),
    occupancyRate: spaces ? roundPercent(occupiedSpaces / spaces) : 0,
    totalMarketValue,
    totalAcquisitionCost,
    unrealisedGain: roundMoney(totalMarketValue - totalAcquisitionCost),
    totalLoanBalance,
    totalEquity: calculateEquity(totalMarketValue, totalLoanBalance),
    portfolioLVR: calculateLVR(totalLoanBalance, totalMarketValue),
    annualRent,
    annualExpenses,
    annualInsurance,
    netOperatingIncome: calculateNetOperatingCashFlow(annualRent, annualExpenses + annualInsurance),
    grossYield: calculateGrossYield(annualRent, totalMarketValue),
    netYield: calculateNetYield(annualRent, annualExpenses + annualInsurance, totalMarketValue),
    totalMonthlyLoanPayments: monthlyLoans,
    cashFlowAfterLoanPayments: calculateCashFlowAfterLoanPayments(annualRent, annualExpenses + annualInsurance, monthlyLoans * 12)
  };
};

export const calculations = {
  roundMoney,
  roundPercent,
  formatCurrencyAUD,
  formatCurrency,
  formatPercentage,
  formatDateAU,
  formatDateWithDayAU,
  getAustralianFinancialYear,
  calculateTenure,
  calculateDaysToExpiry,
  RENT_BASIS_PERIODS_PER_YEAR,
  getRentBasisPeriodsPerYear,
  getRentAnnualisation,
  calculateAnnualizedRent,
  calculatePropertyAcquisitionCost,
  calculatePortfolioCurrentMarketValue,
  calculatePortfolioAcquisitionValue,
  TRANSACTION_CATEGORY_ALIASES,
  normaliseTransactionCategory,
  OPERATING_INCOME_CATEGORIES,
  OPERATING_EXPENSE_CATEGORIES,
  NON_OPERATING_INCOME_CATEGORIES,
  FINANCING_CATEGORIES,
  CAPITAL_CATEGORIES,
  ACQUISITION_CATEGORIES,
  REVIEW_REQUIRED_CATEGORIES,
  HISTORICAL_FY_SUMMARY_MARKERS,
  getHistoricalFySummaryMarker,
  isHistoricalFySummaryTransaction,
  classifyTransactionForPerformance,
  calculateOperatingPerformance,
  getPropertyOperatingPerformance,
  calculateIncome,
  calculateExpenses,
  calculateInsuranceExpenses,
  calculateNetOperatingCashFlow,
  calculateCashFlowAfterLoanPayments,
  calculateEquity,
  calculateLVR,
  calculateGrossYield,
  calculateNetYield,
  calculateOpenItemAge,
  getPropertyLoanPosition,
  calculateTotalLoanBalance,
  calculateTotalMonthlyLoanPayments,
  calculateLoanMonthlyInstallment,
  getPropertyLoanMetrics,
  getPropertyAnticipatedRentMetrics,
  getPropertyIndicativeCashFlowPosition,
  getPropertyNextContractedPosition,
  getPortfolioLoanMetrics,
  calculateAnnualRent,
  getPropertyAnnualRentPosition,
  calculateOccupancyRate,
  calculatePortfolioAnnualRent,
  calculateTransactionTotalsByCategory,
  calculatePropertyInsuranceStatus,
  getPropertySummary,
  getPortfolioSummary
};

export default calculations;